import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Button, Alert, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import * as jobService from '../../Services/jobService';
import HelpModal from '../../Components/Modals/helpModal';
import debounce from 'debounce-promise';
import { useForm } from 'react-hook-form';
import FormRadioButtons from '../ReactHookFormCustom/FormRadioButtons';
import FormAsyncSelect from '../ReactHookFormCustom/FormAsyncSelect';
import FormSelect from '../ReactHookFormCustom/FormSelect';
import FormInput from '../ReactHookFormCustom/FormInput';
import FormTextArea from '../ReactHookFormCustom/FormTextArea';

const EditJobCompetenceModal = ({
  isOpen,
  modalPurpose,
  formCompetences,
  modalIsUsedForApplicantCompetences,
  saveJobCompetence,
  toggle,
  isLoading,
}) => {
  const [competencesForSelect, setCompetencesForSelect] = useState('');
  const [selectedCompetenceType, setSelectedCompetenceType] = useState('Normal');
  const [showHelpModal, setShowHelpModal] = useState(false);
  const { t } = useTranslation();

  const { handleSubmit, reset, control, setValue } = useForm({});

  const debouncedGetCompetences = debounce((inputValue, callback) => {
    getCompetences(inputValue).then((result) => {
      if (callback) callback(result);
    });
  }, 500);

  const promiseOptions = (inputValue, callback) => {
    isOpen && debouncedGetCompetences(inputValue, callback);
  };

  const getCompetences = async (inputValue) => {
    const { data: competences } = await jobService.getCompetencesForSelect(selectedCompetenceType, inputValue);
    if (!competences?.some((option) => option.label?.toLowerCase() === inputValue?.toLowerCase())) {
      competences.push({ value: inputValue, label: inputValue });
    }
    setCompetencesForSelect(competences);
    return competences;
  };

  const radioBoxValueChanged = (value) => {
    setSelectedCompetenceType(value);

    // Update only the asyncSelectCompetence value to null using setValue
    setValue('asyncSelectCompetence', null);
  };

  const handleValidCompetenceSubmit = (values, event) => {
    saveJobCompetence(values);
  };

  const toggleHelpModal = () => {
    setShowHelpModal(!showHelpModal);
  };

  const modalHasOpened = () => {
    if (modalPurpose === 'create') {
      promiseOptions();
    } else {
      const competenceTypeOption = competenceTypeOptions.find(
        (competenceType) => competenceType.value === formCompetences.type
      );

      reset({
        radioBtnsCompetenceType: competenceTypeOption?.value,
        competenceWeight: { value: formCompetences.weight, label: formCompetences.weight },
        competenceNameInput: formCompetences.competenceName,
        competenceDescription: formCompetences.description,
      });
    }
  };

  const modalHasClosed = () => {
    // setIsLoading(false);
  };

  const disabled = modalPurpose === 'view' || modalPurpose === 'edit';

  const competenceTypeOptions = [
    { value: 'Normal', label: t('competences.NORMAL') },
    { value: 'Boolean', label: t('competences.YES_NO') },
    { value: 'Inverse', label: t('competences.INVERSE') },
  ];

  useEffect(() => {
    if (isOpen) {
      // Pre-select the first option when the modal is opened
      reset({
        radioBtnsCompetenceType: competenceTypeOptions[0].value,
        competenceWeight: { value: 5, label: '5' },
      });
    }
  }, [isOpen, reset]);

  // useEffect to call promiseOptions only after selectedCompetenceType changes
  useEffect(() => {
    promiseOptions();
  }, [selectedCompetenceType]);

  return (
    <Modal
      isOpen={isOpen}
      onOpened={modalHasOpened}
      style={{ maxWidth: '600px', width: '100%' }}
      onClosed={modalHasClosed}
      centered
    >
      <ModalHeader>
        <em className="fa-2x icon-rocket mr-2">
          &nbsp;
          {t('competences.COMPETENCE_MANAGEMENT')}
        </em>
        <br />
        <br />
        {!modalIsUsedForApplicantCompetences && <Alert color="success">{t('competences.ADD_OR_REMOVE_MSG')}</Alert>}
        <span>{t('competences.WHAT_TYPE')}</span>
        &nbsp;
        <HelpModal purpose="jobCompetencesHelp" showModal={showHelpModal} onToggle={toggleHelpModal} />
      </ModalHeader>
      <form onSubmit={handleSubmit(handleValidCompetenceSubmit)}>
        <ModalBody>
          <Row className="pb-3">
            <Col className="radio_btn_center-position">
              <FormRadioButtons
                control={control}
                disabled={disabled}
                name="radioBtnsCompetenceType"
                id="radioBtnsCompetenceTypeId"
                options={competenceTypeOptions}
                onChange={(value) => {
                  radioBoxValueChanged(value);
                }}
              />
            </Col>
          </Row>
          <Row className="pb-3">
            <Col md={3}>
              <FormSelect
                control={control}
                name="competenceWeight"
                id="competence-weight-select"
                label={modalIsUsedForApplicantCompetences ? 'Experience' : 'Weight'}
                options={[...Array(10)].map((_, index) => ({
                  value: index + 1,
                  label: (index + 1).toString(),
                }))}
                defaultValue={{ value: 5, label: '5' }}
                placeholder="Select a weight"
                isMulti={false}
                onChange={(selectedOption) => {}}
                rules={{ required: t('common.REQUIRED') }}
              />
            </Col>
            <Col>
              <div id="competenceAsyncSelect">
                {disabled ? (
                  <FormInput
                    control={control}
                    name="competenceNameInput"
                    id="competenceNameInputId"
                    label={t('competences.SELECT_A_COMPETENCE')}
                    disabled={true}
                  />
                ) : (
                  <FormAsyncSelect
                    control={control}
                    name="asyncSelectCompetence"
                    cacheOptions
                    defaultOptions={competencesForSelect}
                    label={t('competences.SELECT_A_COMPETENCE')}
                    loadOptions={promiseOptions}
                    placeholder={t('common.TYPE_TO_SEARCH')}
                    noOptionsMessage={() => t('common.EMPTY_COMPETENCES_MSG')}
                    rules={{ required: t('common.REQUIRED') }}
                  />
                )}
              </div>
              <UncontrolledTooltip
                delay={{ show: 100, hide: 0 }}
                placement="bottom"
                target="competenceAsyncSelect"
                disabled={!disabled}
              >
                {t('competences.DISABLED_DROPDOWN_MSG')}
              </UncontrolledTooltip>
            </Col>
          </Row>
          {!modalIsUsedForApplicantCompetences && (
            <Row>
              <Col>
                <FormTextArea
                  control={control}
                  name="competenceDescription"
                  id="competenceDescriptionId"
                  label={t('common.DESCRIPTION')}
                  maxLength={1000}
                  height="150px"
                />
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter>
          {modalPurpose !== 'view' ? (
            <div>
              <Button color="primary" disabled={isLoading}>
                {isLoading && <div className="spinner-border spinner-border-sm" role="status" />}
                &nbsp;{t('common.SAVE')}
              </Button>
              &nbsp;
              <Button color="secondary" onClick={toggle}>
                {t('common.CANCEL')}
              </Button>
            </div>
          ) : (
            <Button color="primary" onClick={toggle}>
              {t('common.OK')}
            </Button>
          )}
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default EditJobCompetenceModal;
